import type { accountStore, RegisterUserInput } from '~/types/local-types'
import type { CustomerInvoiceOrderBuddy, Maybe, MutationloginOrderBuddyArgs, MutationregisterOrderBuddyArgs, OrderOrderBuddy } from '~/types/orderbuddy-types'
import { apiCall, apiCallMutation } from '~/composables/apiCall'
import { communications } from '~/graphql/mutations/account/communications.mutation.gql'
import { editPassword } from '~/graphql/mutations/account/editPassword.mutation.gql'
import { cancelEmailChange } from '~/graphql/mutations/auth/cancelEmailChange.mutation.gql'
import { confirmEmailChange } from '~/graphql/mutations/auth/confirmEmailChange.mutation.gql'
import { forgotPassword } from '~/graphql/mutations/auth/forgotPassword.mutation.gql'
import { loginUser } from '~/graphql/mutations/auth/login.mutation.gql'
import { logoutUser } from '~/graphql/mutations/auth/logout.mutation.gql'
import { registerUser } from '~/graphql/mutations/auth/register.mutation.gql'
import { resendEmailVerification } from '~/graphql/mutations/auth/resendEmailVerification.mutation.gql'
import { resetPassword } from '~/graphql/mutations/auth/resetPassword.mutation.gql'
import { verifyAccount } from '~/graphql/mutations/auth/verifyAccount.mutation.gql'
import { me } from '~/graphql/queries/me.query.gql'
import { checkout } from '~/store/checkout'
import { CustomerTypeOrderBuddy, MailingTypeOrderBuddy } from '~/types/orderbuddy-types'
import { resetPostHogUser, setPostHogUser } from '~/utils/event-tracker'

const defaultUserData = {
    customer: {
        invAddressSame: true,
        birthday: undefined,
        firstName: '',
        lastName: '',
        email: '',
        phoneGroup: {
            phone: '',
            phoneCountry: 'NL'
        },
        type: 'CONSUMER',
        addressGroup: {
            street: '',
            city: '',
            postalCode: '',
            streetNumber: '',
            country: 'NL'
        },
        billingContact: {
            company: '',
            name: '',
            email: '',
            city: '',
            postalCode: '',
            street: '',
            streetNumber: '',
            country: 'NL',
            phoneGroup: {
                phone: '',
                phoneCountry: 'NL'
            }
        },
        mailing: true,
        smsPref: false,
        pushPref: false,
        emailPref: false,
        payOnInvoiceLimit: 0,
        payOnInvoicePeriod: '',
        payOnInvoiceStatus: null,
        requestedPayOnInvoiceLimit: null,
        requestedPayOnInvoicePeriod: null
    }
}
export const account = defineStore('account', {
    state: (): accountStore => ({
        accountToken: '',
        uuid: null,
        email: '',
        newEmailAwaitingConfirmation: '',
        orders: [],
        invoices: [],
        userData: defaultUserData
    } as accountStore),
    persist: {
        storage: piniaPluginPersistedstate.localStorage()
    },
    getters: {
        getAuthEnabled (): boolean {
            const { public: { authEnabled } } = useRuntimeConfig()
            if (authEnabled) {
                return authEnabled
            } else {
                return false
            }
        },
        isLoggedIn (): boolean {
            return this.getAuthEnabled && this.accountToken.length > 1
        },
        hasBasicAddress (): boolean {
            return this.isLoggedIn && this.userData.customer.firstName !== '' && this.userData.customer.lastName !== '' && this.userData.customer.addressGroup.streetNumber !== '' && this.userData.customer.addressGroup.postalCode !== ''
        },
        basicAddress (state) {
            return {
                firstName: state.userData.customer.firstName,
                lastName: state.userData.customer.lastName,
                postalCode: state.userData.customer.addressGroup.postalCode,
                streetNumber: state.userData.customer.addressGroup.streetNumber
            }
        }
    },
    actions: {
        async setAccountToken (accountToken: string) {
            this.accountToken = accountToken
        },
        setOrders (orders: Maybe<Array<Maybe<OrderOrderBuddy>>>) {
            this.orders = orders?.toSorted((a, b) => {
                return Number(b?.id) - Number(a?.id)
            }) || []
        },
        setInvoices (invoices: Maybe<Array<Maybe<CustomerInvoiceOrderBuddy>>>) {
            this.invoices = invoices?.toSorted((a, b) => {
                return Number(b?.id) - Number(a?.id)
            }) || []
        },
        async loginUser (data: any) {
            const variables: MutationloginOrderBuddyArgs = {
                input: {
                    email: data.email ?? '',
                    password: data.password ?? ''
                }
            }
            const { mutate } = apiCallMutation(loginUser, variables)
            await mutate().then(async response => {
                if (response?.data?.login?.token) {
                    this.setAccountToken(response.data.login.token)
                    await this.getUserData()
                    await nextTick()
                    checkout().autoFillCheckoutUser()
                }
                if (response?.data?.login?.user?.uuid) {
                    this.uuid = response.data.login.user.uuid
                    setPostHogUser(response.data.login.user.uuid)
                }
            })
        },
        async registerUser (newUser: RegisterUserInput) {
            const variables: MutationregisterOrderBuddyArgs = {
                input: {
                    email: newUser.email ?? '',
                    password: newUser.password ?? '',
                    hasAgreedToTerms: newUser.hasAgreedToTerms ?? false,
                    customer: {
                        upsert: {
                            ...(newUser.phoneGroup.phone && newUser.phoneGroup.phoneCountry && {
                                phone: newUser.phoneGroup.phone,
                                phoneCountry: newUser.phoneGroup.phoneCountry
                            }),
                            ...(newUser.requestPayOnInvoice && newUser.payOnInvoicePeriod && {
                                requestPayOnInvoice: newUser.requestPayOnInvoice,
                                payOnInvoiceLimit: newUser.payOnInvoiceLimit ?? 0,
                                payOnInvoicePeriod: newUser.payOnInvoicePeriod
                            })
                        }
                    }
                }
            }
            const { mutate } = apiCallMutation(registerUser, variables)
            await mutate()
        },
        async verifyAccount (email: string, token: string) {
            const variables = { email: email ?? '', token: token ?? '' }
            const { mutate } = apiCallMutation(verifyAccount, variables)
            return await mutate()
        },
        async confirmEmailChange (email: string, token: string) {
            const variables = { email: email ?? '', token: token ?? '' }
            const { mutate } = apiCallMutation(confirmEmailChange, variables)
            return await mutate()
        },
        async cancelEmailChange (email: string) {
            const variables = { email: email ?? '' }
            const { mutate } = apiCallMutation(cancelEmailChange, variables, true)
            return await mutate()
        },
        async communicationPreferences (data: any) {
            const { mutate } = apiCallMutation(communications, data)
            return await mutate()
        },
        async forgotPassword (email: string) {
            const variables = { email: email ?? '' }
            const { mutate } = apiCallMutation(forgotPassword, variables)
            await mutate()
        },
        async resendEmailVerify (email: string) {
            const variables = { email: email ?? '' }
            const { mutate } = apiCallMutation(resendEmailVerification, variables)
            await mutate()
        },
        async resetPassword (data: any) {
            const variables = { email: data.email ?? '', password: data.password ?? '', token: data.token ?? '' }
            const { mutate } = apiCallMutation(resetPassword, variables)
            await mutate()
        },
        async editPassword (data: any) {
            const variables = { password: data.password ?? '', newPassword: data.newPassword ?? '' }
            const { mutate } = apiCallMutation(editPassword, variables, true)
            await mutate()
        },
        async logoutUser (isUnauthenticated: boolean = false) {
            const localePath = useLocalePath()
            this.$reset()
            checkout().resetCopiedCheckoutUser()
            resetPostHogUser()
            if (isUnauthenticated) {
                navigateTo(localePath(`/login${isUnauthenticated ? '?isUnauthenticated=true' : ''}`), { external: true })
            } else {
                const { mutate } = apiCallMutation(logoutUser, {}, true)
                await mutate().then(() => {
                    navigateTo(localePath('/login'))
                })
            }
        },
        async getUserData () {
            if (this.isLoggedIn) {
                const data = await apiCall(me, 'me', {}, false, true)
                if (data) {
                    if (!this.uuid) {
                        this.uuid = data.uuid
                        setPostHogUser(data.uuid)
                    }
                    this.setOrders(data.customer.orders)
                    this.setInvoices(data.customer.invoices)
                    this.email = data.email
                    this.newEmailAwaitingConfirmation = data.newEmailAwaitingConfirmation
                    this.userData = {
                        customer: {
                            type: data.customer.type || CustomerTypeOrderBuddy.CONSUMER,
                            company: data.customer.company,
                            firstName: data.customer.firstName,
                            lastName: data.customer.lastName,
                            email: data.email,
                            birthdate: data.customer.birthdate,
                            phoneGroup: {
                                phone: data.customer.phone,
                                phoneCountry: data.customer.phoneCountry || 'NL'
                            },
                            addressGroup: {
                                street: data.customer.street,
                                city: data.customer.city,
                                postalCode: data.customer.postalCode,
                                streetNumber: data.customer.streetNumber,
                                country: data.customer.country || 'NL'
                            },
                            invAddressSame: !data.customer.hasBillingContact,
                            billingContact: {
                                company: data.customer.billingContact.company,
                                name: data.customer.billingContact.name,
                                email: data.customer.billingContact.email,
                                city: data.customer.billingContact.city,
                                postalCode: data.customer.billingContact.postalCode,
                                street: data.customer.billingContact.street,
                                streetNumber: data.customer.billingContact.streetNumber,
                                country: data.customer.billingContact.country || 'NL',
                                phoneGroup: {
                                    phone: data.customer.billingContact.phone,
                                    phoneCountry: data.customer.billingContact.phoneCountry || 'NL'
                                }
                            },
                            mailing: [ MailingTypeOrderBuddy.SMS, MailingTypeOrderBuddy.PUSH, MailingTypeOrderBuddy.EMAIL ].every(type =>
                                data.customer.communicationPreferences?.includes(type)
                            ),
                            smsPref: !!data.customer.communicationPreferences?.includes(MailingTypeOrderBuddy.SMS),
                            pushPref: !!data.customer.communicationPreferences?.includes(MailingTypeOrderBuddy.PUSH),
                            emailPref: !!data.customer.communicationPreferences?.includes(MailingTypeOrderBuddy.EMAIL),
                            payOnInvoiceStatus: data.customer.payOnInvoiceStatus,
                            payOnInvoiceLimit: data.customer.payOnInvoiceLimit,
                            payOnInvoicePeriod: data.customer.payOnInvoicePeriod,
                            requestedPayOnInvoiceLimit: data.customer.requestedPayOnInvoiceLimit,
                            requestedPayOnInvoicePeriod: data.customer.requestedPayOnInvoicePeriod
                        }
                    }
                }
            }
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(account, import.meta.hot))
}
